import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ArrowRightOutlinedIcon from '@mui/icons-material/ArrowRightOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { PeopleOutlineOutlined, PhoneOutlined, PlaceOutlined } from '@mui/icons-material';

import { Box, DialogContent, DialogTitle, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Business_Profile_Enum, Contract_Status_Enum } from '@kheops/graphql';
import SizedImage from '../../../common/components/SizedImage';
import FileUtils from '../../../common/utils/file.utils';
import ContractStatusTag from '../../../search/ContractStatusTag';
import { getGMapUrlForAddress } from '../../../common/utils/common.utils';
import CompanyLogo from '../../../common/components/CompanyLogo';
import DialogCloseButton from '../../../common/components/DialogCloseButton';
import { BuyerInfoViewProps } from './BuyerInfoDialog';

export default function BuyerInfoGeneralView({ company, close, changeView }: BuyerInfoViewProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'contracts']);
  const companyAddress = company.addresses[0];
  const photo = company.photos && company.photos[0];

  return (
    <>
      <DialogCloseButton onClick={close} />
      <DialogTitle variant="image">
        <SizedImage
          src={FileUtils.BuildCompanyImageUrl({
            photo: photo?.photo,
            type: Business_Profile_Enum.Buyer,
          })}
          alt={t('common:company')}
          width="100%"
          sx={{
            '& > div': { width: '100%' },
            '& img:not([class^="Kheops-companyLogo"]):not([class*=" Kheops-companyLogo"])': {
              maxHeight: 'unset',
              aspectRatio: '3 / 1',
              objectFit: 'cover',
              width: '100%',
            },
          }}
          borderRadius={0}
          overlay={
            !!company.brand && (
              <Box sx={{ position: 'absolute', bottom: { xs: 8, sm: 16 }, left: { xs: 8, sm: 16 }, zIndex: 100 }}>
                <CompanyLogo brand={company.brand} variant="square" size="large" sx={{ width: { xs: 56, sm: 72 } }} />
              </Box>
            )
          }
        />
      </DialogTitle>
      <DialogContent sx={{ maxWidth: 500, p: 0 }}>
        <Box sx={{ px: 2, pt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="titleLarge">
            <ContractStatusTag
              status={Contract_Status_Enum.Signed}
              size={24}
              sx={{ verticalAlign: 'middle', mr: 1 }}
            />
            {company.tradeName}
          </Typography>
          <List>
            <ListItemButton onClick={() => changeView('CONTACTS')}>
              <ListItemIcon>
                <PeopleOutlineOutlined />
              </ListItemIcon>
              <ListItemText
                primary={t('contracts:company_contacts')}
              />
              <ListItemIcon>
                <ArrowRightOutlinedIcon />
              </ListItemIcon>
            </ListItemButton>
            <Divider />
            <ListItemButton onClick={() => changeView('DELIVERY')}>
              <ListItemIcon>
                <LocalShippingOutlinedIcon />
              </ListItemIcon>
              <ListItemText
                primary={t('common:delivery')}
              />
              <ListItemIcon>
                <ArrowRightOutlinedIcon />
              </ListItemIcon>
            </ListItemButton>
            <Divider />
            <ListItemButton onClick={() => changeView('INVOICING')}>
              <ListItemIcon>
                <ReceiptLongOutlinedIcon />
              </ListItemIcon>
              <ListItemText
                primary={t('common:billing')}
              />
              <ListItemIcon>
                <ArrowRightOutlinedIcon />
              </ListItemIcon>
            </ListItemButton>
            <Divider />
            <ListItemButton href={getGMapUrlForAddress(companyAddress.formattedAddress)} target="_blank">
              <ListItemIcon>
                <PlaceOutlined />
              </ListItemIcon>
              <ListItemText
                primary={companyAddress.formattedAddress}
              />
              <ListItemIcon>
                <OpenInNewOutlinedIcon />
              </ListItemIcon>
            </ListItemButton>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <PhoneOutlined />
              </ListItemIcon>
              <ListItemText
                primary={company.contactPhoneNumber}
              />
            </ListItem>
          </List>
        </Box>
      </DialogContent>
    </>
  );
}
